import { Privileges, useUserConfigurationsContext } from '@reapit-sales/api-v2'
import { useLocation } from 'react-router'
import { buildDashboardPageURL } from '../../components/dashboard/buildDashboardPageURL'
import { RouteToPrivilegeKey } from '../constants'
import { buildOfficePageURL } from '../../components/office/buildOfficePageURL'

/**
 * Retrieves a specific privilege configuration from the privileges object.
 *
 * Looks up the privilege entry by its key and returns its access configuration.
 * Useful for checking whether a user can access a specific feature or page.
 *
 * @param {string} privilegeKey - The key name of the privilege to retrieve (e.g., "dashboard", "contacts").
 * @param {Privileges} [privileges] - The full set of user privileges.
 * @returns {{ canAccess?: boolean } | undefined} The privilege configuration object, or undefined if not found.
 */
const getPrivilegeConf = (privilegeKey: string, privileges?: Privileges): { canAccess?: boolean } | undefined => {
  return privileges?.[privilegeKey] as { canAccess?: boolean } | undefined
}

/**
 * Determines the default navigation path based on user privileges.
 *
 * Checks if the user has access to the dashboard page via the `privileges` object.
 * If they have access, returns the dashboard URL; otherwise returns the office URL.
 *
 * @param {Privileges} [privileges] - The privileges object that defines the user's access rights.
 * @returns {string} The URL string of the default page the user should be redirected to.
 */
const getDefaultPath = (privileges?: Privileges): string => {
  const privilegeKey = RouteToPrivilegeKey[buildDashboardPageURL()]
  const privilege = getPrivilegeConf(privilegeKey, privileges)
  if (privilege?.canAccess) {
    return buildDashboardPageURL()
  }
  return buildOfficePageURL()
}

interface usePrivilegeCheckReturnValue {
  /** The privilege configuration for the current path, if available. */
  privilege?: { canAccess?: boolean }

  /** The path currently being accessed or checked. */
  currentPath: string

  /** The default path to redirect to if access is not granted. */
  defaultPath: string
}

/**
 * Hook to check user privileges for the current route.
 *
 * This hook determines whether the user has access to the current path
 * based on the application's privilege configuration.
 *
 * @returns {usePrivilegeCheckReturnValue} An object containing:
 * - `privilege`: The privilege configuration for the current path.
 * - `currentPath`: The current route pathname.
 * - `defaultPath`: The fallback path if the user does not have access to the current path.
 *
 * @example
 * const { privilege, currentPath, defaultPath } = usePrivilegeCheck()
 * if (!privilege) {
 *   navigate(defaultPath)
 * }
 */
export const usePrivilegeCheck = (): usePrivilegeCheckReturnValue => {
  const location = useLocation()
  const { privileges } = useUserConfigurationsContext()

  const path = location.pathname

  // Pairing path to privileges configuration; if not found, assume global access.
  const privilegeKey = RouteToPrivilegeKey[path]

  // Get privileges configuration for current path
  const privilege = getPrivilegeConf(privilegeKey, privileges)

  // Get default path if access to current path is denied
  const defaultPath = getDefaultPath(privileges)

  return {
    privilege,
    currentPath: path,
    defaultPath: defaultPath,
  }
}
